import { useState, useMemo } from 'react';
import { validateDomainName, isJsonFileEmpty } from './utils';
// import { TrackingDomain, SendingDomain, AppSiteAssociations } from './types';

export const useValidatedDomain = (
  initialDomainName = '',
  domains /*: Array<SendingDomain | TrackingDomain | AppSiteAssociations> */ = [],
  validationKey = 'domain',
) => {
  const [domainName, setDomainName] = useState(initialDomainName);

  // for appSiteAssociations, validationKey === 'appDomain'
  // for tracking or sending domains, validationKey === 'domain'
  const domainNames = useMemo(() => domains.map((domain) => domain[validationKey]), [domains]);

  const errorMessage = useMemo(
    () => validateDomainName(domainName, domainNames),
    [domainName, domainNames],
  );

  return [domainName, setDomainName, errorMessage];
};

// finds the currently displayed tracking domain in
// the SetTrackingDomainModal dropdown state.
// Uses the first tracking domain if the sending domain
// does not have a tracking domain alreaduy assigned to it
export const useTrackingDomain = (
  sendingDomain /*: SendingDomain */ = {},
  trackingDomains /*: Array<TrackingDomain> */ = [],
) => {
  const currentTrackingDomain = useMemo(() => {
    const hasTrackingDomain = sendingDomain.trackingDomain;
    if (!hasTrackingDomain) {
      return trackingDomains[0];
    }
    return trackingDomains.find(({ domain }) => domain === sendingDomain.trackingDomain);
  }, [trackingDomains]);

  const [trackingDomain, setTrackingDomain] = useState(currentTrackingDomain);

  return [trackingDomain, setTrackingDomain];
};

// glorified useState that takes in a json string
// and verifies whether or not any changes to it result
// in a valid json string as well.
export const useValidJson = (jsonString = '') => {
  const startingState = isJsonFileEmpty(jsonString) ? '' : JSON.stringify(jsonString, null, 2);
  const [json, setJson] = useState(startingState);
  const hasError = useMemo(() => {
    // don't want to show an error if there is no input string
    if (json === '') {
      return false;
    }

    try {
      JSON.parse(json);
      return false;
    } catch (e) {
      return true;
    }
  }, [json]);

  return [json, setJson, hasError];
};
